import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useLang } from './LanguageProvider';
import translations from '../translations';

/**
 * HUKUKİ BELGE KABUĞU — TEK KAYNAK.
 *
 * Dört hukuki sayfa (Kullanım Koşulları, Gizlilik, Çerez, KVKK) aynı
 * yapıyı paylaşıyor: rozet · başlık · tarih · giriş · numaralı maddeler.
 * Dört ayrı dosyada aynı JSX'i çoğaltmak yerine tek bileşen; sayfalar
 * yalnızca `sozlukAnahtari` ve renk veriyor.
 *
 * Neden `max-w-3xl`: uzun hukuki metinde okunabilirliğin ölçüsü satır
 * uzunluğudur. 3xl (48rem) gövde punto ölçüsünde satır başına yaklaşık
 * 75-85 karaktere denk gelir; tipografi ölçütünün üst sınırı budur.
 * Daha geniş bir kap, metni okunmaz değil ama takip edilemez yapar.
 *
 * `whitespace-pre-line`: sözlükteki bazı paragraflar (ör. şirket
 * künyesi) satır sonu içeriyor; onlar korunuyor.
 */
export default function LegalDoc({ sozlukAnahtari, parlaklikRengi }) {
  const { lang } = useLang();
  const t = translations[lang][sozlukAnahtari];
  const ortak = translations[lang].common;

  return (
    <div className="min-h-screen dark:bg-slate-950 bg-[#f0f4f8] dark:text-white text-slate-900 pt-20">
      {/* Arka plan halesi — sayfanın kendisi kaydırılırken sabit kalır,
          tıklamayı yakalamaz. */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-1/4 -left-32 w-96 h-96 bg-cyan-500/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 -right-32 w-96 h-96 bg-purple-500/5 rounded-full blur-3xl" />
      </div>

      <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-20">
        {/* Geri bağlantısı */}
        <div className="mb-8 sm:mb-10">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm dark:text-white/50 text-slate-500 hover:dark:text-white hover:text-slate-900 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            {ortak.home}
          </Link>
        </div>

        {/* Başlık bloğu */}
        <header className="mb-10 sm:mb-12">
          {/* ROZET KENDİ SATIRINDA — sarmalayıcı `div` ZORUNLU.
              Rozet `inline-block`, başlıktaki `.animated-gradient-text` de
              `display: inline-block` (App.css:29 — Türkçe ş/ç/ğ harflerinin
              alt uzantıları kırpılmasın diye böyle). İki `inline-block`
              kardeş aynı satıra oturur: rozet başlığın SOLUNA sıkışıyor,
              `mb-4` de aynı satırda kaldığı için hiçbir ayrım yaratmıyordu.
              Blok bir sarmalayıcı satırı kesin olarak bitirir; alt boşluk
              artık başlıkla rozetin arasında gerçekten oluşur. */}
          <div className="mb-4">
            <span className="inline-block px-3 py-1 rounded-full text-[11px] font-semibold tracking-wide uppercase border dark:border-cyan-500/30 border-cyan-500/40 dark:bg-cyan-500/10 bg-cyan-50 dark:text-cyan-300 text-cyan-700">
              {t.badge}
            </span>
          </div>
          <h1 className="text-[26px] leading-tight sm:text-4xl lg:text-5xl font-bold mb-3 animated-gradient-text break-words">
            {t.title}
          </h1>
          <p className="text-xs sm:text-sm dark:text-white/40 text-slate-500">{t.lastUpdated}</p>
          <p className="mt-5 text-sm sm:text-[15px] leading-relaxed dark:text-white/60 text-slate-600 break-words">
            {t.intro}
          </p>
        </header>

        {/* Maddeler */}
        <div className="space-y-5 sm:space-y-6">
          {t.sections.map((s, i) => (
            <section
              key={i}
              className="glow-card rounded-2xl p-5 sm:p-7 lg:p-8"
              style={{ '--glow-color': parlaklikRengi }}
            >
              {/* Madde numarası başlığın içinde geliyor (sözlükte "1. ..."
                  biçiminde). Hizayı bozmamak için ayrı bir sütun açılmadı;
                  dar ekranda numaranın metni içeri itmesi engellenmiş olur. */}
              <h2 className="text-[15px] sm:text-base lg:text-lg font-bold mb-3 dark:text-white text-slate-900 break-words">
                {s.h}
              </h2>
              <div className="space-y-3">
                {s.p.map((paragraf, j) => (
                  <p
                    key={j}
                    className="text-[13px] sm:text-sm leading-relaxed dark:text-white/60 text-slate-600 whitespace-pre-line break-words"
                  >
                    {paragraf}
                  </p>
                ))}
              </div>
            </section>
          ))}
        </div>
      </div>
    </div>
  );
}
